import {
    ArrowSquareOut,
    CloudArrowUp,
    Key,
    Note,
    ShieldCheck,
    WarningCircle,
} from '@phosphor-icons/react';
import { Button, Label, TextInput, Tooltip } from 'flowbite-react';
import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { APP_PLATFORM_LOGO_MAP } from '../../../../shared/constants';
import type { AppleIAPFormData } from '../interface';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import type { Resolver } from 'react-hook-form';
import type { IIAPConnectResponse } from '../../../../interfaces';
import AnimatedModal from '../../../../components/AnimatedModal';

interface AppleIAPModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Calls the connect API and returns the response */
    onConnect: (formData: AppleIAPFormData) => Promise<IIAPConnectResponse>;
    /** Called after the flow completes */
    onSuccess: (selectedAppIds: string[]) => void;
}

// Validation schema
const getAppleIAPSchema = (t: TFunction) => yup.object().shape({
    p8File: yup
        .mixed()
        .required(t('configurations.integrations.appleIap.errors.p8Required')),
    keyId: yup
        .string()
        .required(t('configurations.integrations.appleIap.errors.keyIdRequired'))
        .matches(/^[A-Z0-9]{10}$/i, t('configurations.integrations.appleIap.errors.keyIdInvalid')),
    issuerId: yup
        .string()
        .required(t('configurations.integrations.appleIap.errors.issuerIdRequired'))
        .matches(
            /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/,
            t('configurations.integrations.appleIap.errors.issuerIdInvalid')
        ),
    vendorNumber: yup
        .string()
        .required(t('configurations.integrations.appleIap.errors.vendorRequired'))
        .matches(/^\d+$/, t('configurations.integrations.appleIap.errors.vendorInvalid')),
});

const AppleIAPModal: React.FC<AppleIAPModalProps> = ({
    isOpen,
    onClose,
    onConnect,
    onSuccess,
}) => {
    const { t } = useTranslation();

    // ── Form state ──────────────────────────────────────────────────────────
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [fileError, setFileError] = useState<string | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const [isConnecting, setIsConnecting] = useState(false);
    const [connectError, setConnectError] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        reset,
        clearErrors,
    } = useForm<AppleIAPFormData>({
        resolver: yupResolver(getAppleIAPSchema(t)) as unknown as Resolver<AppleIAPFormData>,
        defaultValues: { p8File: null, keyId: '', issuerId: '', vendorNumber: '' },
    });

    // ── Handlers ────────────────────────────────────────────────────────────
    const handleClose = () => {
        reset();
        setSelectedFile(null);
        setFileError(null);
        setConnectError(null);
        onClose();
    };

    const validateAndSetFile = (file?: File) => {
        setFileError(null);
        clearErrors('p8File');
        if (!file) { setFileError(t('configurations.integrations.appleIap.errors.p8Select')); return; }
        if (!file.name.endsWith('.p8')) { setFileError(t('configurations.integrations.appleIap.errors.p8Invalid')); return; }
        if (file.size > 2 * 1024 * 1024) { setFileError(t('configurations.integrations.appleIap.errors.fileSize')); return; }
        setSelectedFile(file);
        setValue('p8File', file);
    };

    const onFormSubmit = async (data: AppleIAPFormData) => {
        if (!selectedFile) { setFileError(t('configurations.integrations.appleIap.errors.p8Select')); return; }

        setIsConnecting(true);
        setConnectError(null);

        try {
            await onConnect({
                p8File: selectedFile,
                keyId: data.keyId,
                issuerId: data.issuerId,
                vendorNumber: data.vendorNumber,
            });
            onSuccess([]);
            handleClose();
        } catch (err: unknown) {
            setConnectError(err instanceof Error ? err.message : t('configurations.integrations.errors.connectAppleIAP', 'Failed to connect. Please try again.'));
        } finally {
            setIsConnecting(false);
        }
    };

    const externalLinks = {
        keyId: 'https://appstoreconnect.apple.com/access/integrations/api',
        issuerId: 'https://appstoreconnect.apple.com/access/integrations',
        vendorId: 'https://appstoreconnect.apple.com/trends/',
        supportGuide: '/support#apple',
    };

    // ── Modal title ──────────────────────────────────────────────────────────
    const modalTitle = (
        <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-gray-900 rounded-xl flex items-center justify-center flex-shrink-0">
                <img src={APP_PLATFORM_LOGO_MAP.apple} alt="Apple" className="w-8 h-8 invert" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <h3 className="text-lg font-semibold text-gray-900">{t('configurations.integrations.apple_iap')}</h3>
                    <Tooltip content={t('configurations.integrations.viewGuide')} placement="right">
                        <Note size={20} weight="duotone" className="text-gray-500 cursor-pointer"
                            onClick={() => window.open(externalLinks.supportGuide, '_blank')} />
                    </Tooltip>
                </div>
                <div className="flex items-center gap-1.5 mt-0.5">
                    <ShieldCheck size={13} className="text-indigo-600 flex-shrink-0" />
                    <p className="text-[11px] text-gray-500">{t('configurations.integrations.credentialsSecure')}</p>
                </div>
            </div>
        </div>
    );

    return (
        <AnimatedModal show={isOpen} onClose={handleClose} size="lg" title={modalTitle}>
            <div className="relative">
                <form onSubmit={handleSubmit(onFormSubmit)} className="space-y-4">
                    {/* File Upload */}
                    <div>
                        <Label htmlFor="p8File" className="flex items-center gap-1 mb-2">
                            <Key size={14} className="text-indigo-600" />
                            API Key File (.p8)
                        </Label>
                        <div
                            onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
                            onDragLeave={(e) => { e.preventDefault(); setIsDragging(false); }}
                            onDrop={(e) => { e.preventDefault(); setIsDragging(false); validateAndSetFile(e.dataTransfer.files?.[0]); }}
                            onClick={() => fileInputRef.current?.click()}
                            className={`border-2 border-dashed rounded-xl px-4 py-2 text-center cursor-pointer transition-all ${isDragging ? 'border-indigo-500 bg-indigo-50' : selectedFile ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:border-gray-300'
                                }`}
                        >
                            <CloudArrowUp size={32} className={`mx-auto mb-2 ${selectedFile ? 'text-emerald-600' : 'text-gray-400'}`} />
                            {selectedFile ? (
                                <p className="text-sm font-medium text-emerald-700">{selectedFile.name}</p>
                            ) : (
                                <>
                                    <p className="text-sm text-gray-600">{t('configurations.integrations.dropFile', { ext: '.p8' })}</p>
                                    <p className="text-xs text-gray-400 mt-1">{t('configurations.integrations.appleIap.note')}</p>
                                </>
                            )}
                            <input ref={fileInputRef} type="file" accept=".p8" onChange={(e) => validateAndSetFile(e.target.files?.[0])} className="hidden" />
                        </div>
                        {(fileError || errors.p8File) && (
                            <p className="text-sm text-red-600 mt-1">{fileError || (errors.p8File?.message as string)}</p>
                        )}
                    </div>

                    {/* Key ID */}
                    <div>
                        <Label htmlFor="keyId" className="flex items-center gap-1 mb-2">
                            <Key size={14} className="text-indigo-600" />
                            Key ID
                        </Label>
                        <TextInput id="keyId" placeholder="2SD45F567" {...register('keyId')} color={errors.keyId ? 'failure' : undefined} />
                        {errors.keyId && <p className="text-sm text-red-600 mt-1">{errors.keyId.message}</p>}
                        <a href={externalLinks.keyId} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 mt-1">
                            <ArrowSquareOut size={12} />
                            Open App Store Connect → Users and Access → Integrations → Keys
                        </a>
                    </div>

                    {/* Issuer ID */}
                    <div>
                        <Label htmlFor="issuerId" className="flex items-center gap-1 mb-2">
                            <Key size={14} className="text-indigo-600" />
                            Issuer ID
                        </Label>
                        <TextInput id="issuerId" placeholder="9T64565H77TH4DF" {...register('issuerId')} color={errors.issuerId ? 'failure' : undefined} />
                        {errors.issuerId && <p className="text-sm text-red-600 mt-1">{errors.issuerId.message}</p>}
                        <a href={externalLinks.issuerId} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 mt-1">
                            <ArrowSquareOut size={12} />
                            Open App Store Connect → Users and Access → Integrations
                        </a>
                    </div>

                    {/* Vendor ID */}
                    <div>
                        <Label htmlFor="vendorNumber" className="flex items-center gap-1 mb-2">
                            <Key size={14} className="text-indigo-600" />
                            Apple Vendor ID
                        </Label>
                        <TextInput id="vendorNumber" placeholder="123456789" {...register('vendorNumber')} color={errors.vendorNumber ? 'failure' : undefined} />
                        {errors.vendorNumber && <p className="text-sm text-red-600 mt-1">{errors.vendorNumber.message}</p>}
                        <a href={externalLinks.vendorId} target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 text-xs text-indigo-600 hover:text-indigo-700 mt-1">
                            <ArrowSquareOut size={12} />
                            Open App Store Connect → Sales and Trends → Vendor ID
                        </a>
                    </div>

                    {/* Note Banner */}
                    <div className="rounded-xl bg-amber-50 border border-amber-200 p-2 text-xs text-amber-800 leading-relaxed">
                        <p className="font-semibold mb-1">{t('validation.importantNote')}</p>
                        {t('configurations.integrations.appleIapNote')}
                    </div>

                    {/* Connect error */}
                    {connectError && (
                        <div className="flex items-start gap-2 p-3 rounded-xl bg-red-50 border border-red-200">
                            <WarningCircle size={16} className="text-red-500 flex-shrink-0 mt-0.5" />
                            <p className="text-xs text-red-700">{connectError}</p>
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                        <Button color="light" onClick={handleClose} disabled={isConnecting}>
                            {t('common.cancel')}
                        </Button>
                        <Button color="primary" type="submit" disabled={isConnecting} isProcessing={isConnecting}>
                            <div className="flex items-center gap-2">
                                <ShieldCheck size={16} />
                                {t('configurations.integrations.connectServiceAccount')}
                            </div>
                        </Button>
                    </div>
                </form>
            </div>
        </AnimatedModal>
    );
};

export default AppleIAPModal;
